import React, { FC, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { NavigateWithQuery } from '@dxb-web-ui-framework/common/components/NavigateWithQuery/NavigateWithQuery';
import { AccountExtraitTransactionDetailsProviderWrapper } from '../../../packages/transaction/src/contexts/AccountExtraitTransactions/AccountExtraitTransactionDetailsProvider';
import { EligibleRoute, SecureRoute } from '../../../packages/iam/src/components';
import { AccountDetailsProvider } from '../../../packages/account/src/contexts/AccountDetails/AccountDetailsProvider';
import { EnhancedAliasProvider } from '../../../packages/account/src/contexts/Alias/AliasProvider';
import {
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../packages/userProfile/src/contexts/UserInfo/hooks';

const AccountDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AccountDetailsPageComponent" */ '../../../packages/account/src/pages/AccountDetailsPage'
    ),
);

const TopUpAccountPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AccountDetailsPageComponent" */ '../../../packages/account/src/pages/TopUpAccountPage'
    ),
);

const AccountExtraitTransactionsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AccountExtraitTransactionsPageComponent" */ '../../../packages/transaction/src/pages/AccountExtraitTransactionsPage'
    ),
);

const AccountExtraitTransactionDetailsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "AccountExtraitTransactionDetailsPageComponent" */ '../../../packages/transaction/src/pages/AccountExtraitTransactionDetailsPage'
    ),
);

const AccountRouter: FC<GenericObject> = ({ brandConfig, requiredEntitlements, logic }) => {
  const location = useLocation();
  const customerId = useSelectedCustomerId();
  const isAllowed = useUserInfoIsUserAllowedAction(requiredEntitlements, logic);

  if (!isAllowed) {
    return <NavigateWithQuery to={`/${customerId}/dashboard`} replace state={{ from: location }} />;
  }
  return (
    <AccountDetailsProvider>
      <Routes>
        <Route
          path='/details'
          element={
            <SecureRoute requiredEntitlements={UserAppTransaction.DepositAccountDetails}>
              <EligibleRoute>
                <EnhancedAliasProvider>
                  <AccountDetailsPage pageConfig={brandConfig.accountDetailsPage} />
                </EnhancedAliasProvider>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/topup'
          element={
            <SecureRoute requiredEntitlements={UserAppTransaction.AccountTopUp}>
              <EligibleRoute>
                <EnhancedAliasProvider>
                  <TopUpAccountPage pageConfig={brandConfig.accountTopupPage} />
                </EnhancedAliasProvider>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/transactions'
          element={
            <SecureRoute requiredEntitlements={UserAppTransaction.DepositAccounts}>
              <EligibleRoute>
                <AccountExtraitTransactionDetailsProviderWrapper>
                  <AccountExtraitTransactionsPage pageConfig={brandConfig.accountExtraitTransactionsPage} />
                </AccountExtraitTransactionDetailsProviderWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
        <Route
          path='/transaction/:transactionNo/details'
          element={
            <SecureRoute>
              <EligibleRoute>
                <AccountExtraitTransactionDetailsProviderWrapper>
                  <AccountExtraitTransactionDetailsPage pageConfig={brandConfig.accountExtraitTransactionDetailsPage} />
                </AccountExtraitTransactionDetailsProviderWrapper>
              </EligibleRoute>
            </SecureRoute>
          }
        />
      </Routes>
    </AccountDetailsProvider>
  );
};

export { AccountRouter };
